<!-- src/components/ThePathUniverse.vue -->
<template>
  <section class="the-path-universe content-section section">
    <!-- Logo and Photos -->

    <!-- Content Sections -->
    <div class="content">
      <!-- The 33 Path Section -->
      <section class="content-section animate fadeInUp">
        <p class="drop-cap">
          <span class="drop-cap-words serif animate fadeInLeft">The33Path</span>
          is the eponymous universe authored by Patricia Ribeiro. Its narrative
          orbits around the spark at the heart of this reality, where the seen
          and unseen realms converge through the unfolding story of this
          consciousness known as
          <span class="serif bold emphasis animate fadeInRight"> The Path</span
          >, emerging in the significant
          <span class="serif largeText emphasis animate fadeInDown textColour">
            '33' </span
          >stage of her ever-evolving universe.
        </p>
        <p>
          The33Path is a decade-long transmedia universe composed of twelve
          interconnected stories. Across these twelve works, the consciousness
          known as The Path evolves through distinct worlds, identities, and
          transmissions - each representing a phase of a larger cycle known as
          the '33 Transmission.' Over the next ten years, audiences will be
          guided through this macro-story as it unfolds across film, narrative,
          art, performance, and experiential media.
        </p>
        <p>
          Each micro-narrative within The33Path forms a chapter in a larger
          cosmic theatre, where every desire, belief, and intention shapes the
          architecture of its world. Together, they create a multi-layered
          storyworld in which inner transformation becomes cinematic structure.
        </p>

        <blockquote class="animate fadeInRight quote text-right">
          As a multiplayer in life’s simulation game,
          <span class="bigText inlineBlock textColour">
            The Path
            <span class="bigText emphasis serif textColour"> will </span
            ><span class="bigText emphasis serif textColour"> expand</span>,
          </span>

          through the meeting of both conscious and unconscious aspects,
          <span class="bigText emphasis serif textColour"
            >taking us in an intimate journey</span
          >
          while her ego breaks and dissolves in the never-ending layers of many
          selves, transcending the same limitations that had previously defined
          her and opening the way for true authenticity.
        </blockquote>

        <p>
          To become a master of oneself is the true purpose of the quest and The
          33 Path is on a ride to self-mastery.
        </p>

        <!-- Main Longread NDA Note -->
        <MicroNDA variant="mainlongread" />
      </section>

      <!-- Brand's Long-term Plan Section -->
      <section class="content-section animate fadeInUp">
        <p>
          The long-term architecture of The33Path revolves around
          <span class="serif bold emphasis animate textColour fadeInDown"
            >3 characters</span
          >
          who embody Patricia's own evolution - from Karma to Dharma, from sleep
          to awakening, and from objective perception to omnijective awareness.
          By threading the fragmented memories of her past into a canvas of
          creative revelation, The Path learns to surrender into her true
          essence, sacrificing what no longer serves in the name of authenticity
          and inspiring the spectator to do the same through her radical honesty
          and artistic expression.
        </p>
        <p>
          Through the interplay of
          <span class="serif bold emphasis animate fadeInDown"
            >Immersive Visionary Narratives</span
          >
          and absorbing storytelling,
          <span class="textColour animate fadeInDown"
            >the spectator is drawn into this</span
          >
          <span class="serif bold emphasis animate fadeInDown textColour">
            U-niverse</span
          >
          - a vast space operating by its own inner logic, existing parallel to
          the collective world we all inhabit. As the viewer moves closer to the
          other side of the screen, they enter the domain where life imitates
          art and art rewrites the self, until they find themselves woven into
          the same story they once believed they were only watching.
        </p>
        <p>
          As The Path evolves through the twelve works, she expresses different
          archetypal states - each revealing a facet of her expanding
          consciousness.
        </p>

        <!-- <p class="quote serif text-left animate purpleLight fadeInLeft">
          The Path is already conscious of the journey, it has always been, what
          gives us an advantage while the world watches as we go along...
          <br /><span
            class="serif text-right animate fadeInRight purpleLight overflowHidden"
            >each character is a turn in the spiral of existence, allowing the
            spectator to observe silently from afar, their
            <span class="larger-Text animate purpleLighter fadeInUp"
              >consciousness</span
            >
            too making its way to the other side of the screen, to the domain
            where life imitates art and art rewrites the self.</span
          >
        </p> -->
      </section>

      <!-- Include the new components -->
      <ThePathsIdentity />
      <The10YearPlan />

      <!-- The Path's Architecture Section (2.6) -->
      <section class="content-section animate fadeInUp paths-architecture">
        <h2 class="text-center animate fadeIn">The Path's Architecture</h2>
        <p class="animate fadeIn">
          The business architecture of The Path mirrors its creative design: a
          vertically integrated, regenerative ecosystem where
          <span class="textColour paths-arch-highlight"
            >multiple streams of creation</span
          >
          - film, artefacts, technology, narrative, performance, and proprietary
          creative methodology - sustain and amplify one another. Over the first
          decade, the brand expands through seven interconnected business
          divisions that merge art, storytelling, commerce, and creative
          technology into a unified field.
        </p>
        <p class="animate fadeIn">
          The ideal structure for this evolution is a studio-partnered
          incubation model, where IP development and cinematic production grow
          in tandem. The Path aims to collaborate with a prestige studio - A24,
          Searchlight Pictures, Annapurna Pictures, Focus Features, or an
          equivalent partner - to align creative development, production, and
          distribution under one coherent creative and financial structure.
        </p>
        <p class="animate fadeIn">
          This partnership model preserves the integrity of authorship while
          enabling large-scale realisation. Studios provide production scale and
          distribution power; The Path retains IP ownership, narrative
          coherence, and the creative heart that sustains the universe.
          Negotiations are designed as mutual expansions, ensuring that both
          parties grow through the collaboration.
        </p>
        <blockquote class="animate fadeInLeft textColour text-center">
          <strong class="textColourWhite"
            >This multi-phase cycle unfolds across thirteen years</strong
          >
        </blockquote>
        <p class="animate fadeIn">
          Two foundational years of creation and early pre-production, during
          which the initial wave of projects across multiple stories enters
          development; followed by ten years of continuous public releases.
          During this decade, the experience begins with an accelerated rhythm -
          two releases per year - before settling into one major cinematic work
          annually, completing the full twelve-story cycle. The thirteenth year
          culminates in the release of the documentary, conceived to reveal the
          architecture, transmission, and quantum logic behind the universe and
          to close the experiential loop with the audience.
        </p>
        <p class="animate fadeIn">
          This structure allows the public to accompany the evolution of the
          universe in real time - experiencing a sustained rhythm of expansion,
          deepening engagement, and organic community growth within a living
          continuum where story, art, and business unfold as one consciousness.
          Across these thirteen years, The Path forges a living myth that finds
          its final embodiment in the creation of the
          <span class="textColour paths-arch-highlight">Mythic Kingdom</span>
          - a permanent, constructed environment where the universe materialises
          into physical form and all narrative, experiential, and architectural
          layers converge.
        </p>
        <p class="animate fadeIn">
          The entire universe is anchored in a proprietary creative technology
          known as The Helix Creative System™ - a codified method of narrative
          transmission, frequency design, and structural coherence. This system
          ensures that every story, artefact, character arc, and experiential
          layer within The33Path remains aligned with its source consciousness.
        </p>
        <p class="animate fadeIn">
          The first decade of The Path unfolds through the already-structured
          narrative of the three characters, whose evolutions shape the brand's
          creative, experiential, and financial strategy. Within this cycle, the
          business model is supported by seven interconnected streams of
          monetisation, each functioning as a pillar of the universe and a
          pathway through which the audience
          <span class="textColour paths-arch-highlight"
            >enters the storyworld</span
          >.
        </p>
      </section>

      <TheBusinessModel />
    </div>
  </section>
</template>

<script>
import { onMounted, ref } from "vue";
import ThePathsIdentity from "@/components/ThePathUniverse/ThePathsIdentity.vue";
import The10YearPlan from "@/components/The10YearPlan.vue";
import TheBusinessModel from "@/components/TheBusinessModel.vue";
import SwissStyleTable from "@/components/GenericComponents/SwissStyleTable.vue";
import MicroNDA from "@/components/GenericComponents/MicroNDA.vue";

export default {
  name: "ThePathUniverse",
  components: {
    ThePathsIdentity,
    The10YearPlan,
    TheBusinessModel,
    SwissStyleTable,
    MicroNDA,
  },
  setup() {
    const cecilia = ref(require("@/assets/thePath/cecilia.png"));
    const patricia = ref(require("@/assets/thePath/patricia.png"));
    const thePath = ref(require("@/assets/thePath/thePath1.jpg"));

    onMounted(() => {
      // Select all elements with the 'animate' class
      const elements = document.querySelectorAll(".animate");

      const observerOptions = {
        root: null,
        rootMargin: "-10% 0px", // Adjust top margin to center the trigger
        threshold: 0, // Trigger as soon as the top of the element reaches the middle
      };

      const handleIntersection = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Add 'active' class when the top of the element reaches the middle of the screen
            entry.target.classList.add("active");
          } else {
            // Remove 'active' class when it leaves
            entry.target.classList.remove("active");
          }
        });
      };

      const observer = new IntersectionObserver(
        handleIntersection,
        observerOptions
      );

      elements.forEach((el) => {
        if (el) {
          observer.observe(el);
        }
      });
    });

    return { cecilia, patricia, thePath };
  },
};
</script>

<style scoped>
/* Container Styles */
.the-path-universe {
  max-width: 900px;
  margin: 0 auto;
  padding: var(--largeSpace);
}

/* Content Styles */
.content {
  margin-top: 2rem;
}

.statement {
  font-style: italic;
  text-align: center;
  margin: 2rem 0;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: var(--clickableHeight);
}

.tracking-wider {
  letter-spacing: 0.07em;
}

/* Path's Architecture: softer + faster fade-in */
.paths-architecture .animate {
  transition: opacity 0.75s ease, transform 0.75s ease;
}

/* Inline highlights: animate with their parent paragraph */
.paths-architecture .paths-arch-highlight {
  display: inline-block;
  opacity: 0;
  transform: translateY(-20px);
  transition: opacity 0.75s ease, transform 0.75s ease;
}

.paths-architecture p.active .paths-arch-highlight {
  opacity: 1;
  transform: translateY(0);
}
th,
td {
  padding: var(--space) 0;
  text-align: start;
}

/* Responsive Design */
@media (max-width: 768px) {
  .the-path-universe {
    padding: var();
  }
}
</style>
